import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Coins, Key, ArrowRight, User } from 'lucide-react';
import StemCellAnimation from './StemCellAnimation';

interface Step {
  number: number;
  title: string;
  description: string;
  icon?: React.ReactNode;
}

interface ThreeStepGuideProps {
  steps?: Step[];
  variant?: 'default' | 'compact' | 'detailed';
}

const ThreeStepGuide: React.FC<ThreeStepGuideProps> = ({ 
  steps,
  variant = 'default' 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [pixelFaceDataUrl, setPixelFaceDataUrl] = useState<string>('');
  const [animatingCard, setAnimatingCard] = useState<number | null>(null);
  const [titleFontSize, setTitleFontSize] = useState<string>('16px');

  // Calculate responsive font size based on window width - bypasses all CSS conflicts
  useEffect(() => {
    const calculateFontSize = () => {
      const width = window.innerWidth;
      let fontSize = '16px';
      if (width <= 400) {
        fontSize = '14px';
      } else if (width <= 640) {
        fontSize = '15px';
      } else if (width <= 767) {
        fontSize = '16px';
      } else if (width <= 1023) {
        fontSize = '15px'; // CRITICAL: Smaller for narrow 3-column cards
      } else {
        fontSize = '22px';
      }
      setTitleFontSize(fontSize);
    };

    calculateFontSize();
    window.addEventListener('resize', calculateFontSize);
    return () => window.removeEventListener('resize', calculateFontSize);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };
    
    if (showModal) {
      window.addEventListener('keydown', handleEscape);
      return () => {
        window.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showModal]);

  const defaultSteps: Step[] = [
    {
      number: 1,
      title: 'Join the Community',
      description: 'Complete KYC for identity verification. Receive a Member ID then mint an Experience Key.'
    },
    {
      number: 2,
      title: 'Get Your Credits',
      description: 'Your Experience Key includes credits (200-20,000) that renew automatically each year.'
    },
    {
      number: 3,
      title: 'Reserve & Connect',
      description: 'Use credits to reserve properties, trade with members, and vote on DAO direction and projects.'
    }
  ];

  const displaySteps = steps || defaultSteps;

  // Load static Member ID image - load immediately, not just when modal opens
  useEffect(() => {
    // Always set the image path so it's available when modal opens
    setPixelFaceDataUrl('/images/BornFree-Member-ID.png');
  }, []);

  const handleCardClick = (stepNumber: number) => {
    // Card 1: Always opens modal directly
    if (stepNumber === 1) {
      setShowModal(true);
      return;
    }
    
    // Cards 2 & 3: Start animation, then open modal when complete
    if (animatingCard === null) {
      setAnimatingCard(stepNumber);
      // Animation will complete in ~4.9 seconds (14 frames * 350ms)
      // Modal opens via onAnimationComplete callback
    }
  };

  const handleAnimationComplete = () => {
    setShowModal(true);
    setAnimatingCard(null);
  };

  if (variant === 'compact') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {displaySteps.map((step) => (
          <div
            key={step.number}
            className="text-center pt-8 pb-4 px-6 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:border-cyan-400/50 transition-all duration-300"
          >
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-xl"></div>
                <div className="relative bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full w-16 h-16 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                  {step.number}
                </div>
              </div>
            </div>
            <h3 className="text-xl font-bold text-white mb-2 whitespace-nowrap">{step.title}</h3>
            <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'detailed') {
    return (
      <div className="space-y-8 max-w-4xl mx-auto">
        {displaySteps.map((step, index) => (
          <div
            key={step.number}
            className="flex flex-col md:flex-row gap-6 items-start pt-8 pb-4 px-6 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:border-cyan-400/50 transition-all duration-300"
          >
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-xl"></div>
                <div className="relative bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full w-20 h-20 flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                  {step.number}
                </div>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3 break-words">{step.title}</h3>
              <p className="text-gray-300 leading-relaxed">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default variant with stem cell animation
  return (
    <>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mt-8">
        {displaySteps.map((step) => {
          const isCard1 = step.number === 1;
          const isCardAnimating = animatingCard === step.number;
          
          return (
            <div
              key={step.number}
              className="relative group cursor-pointer"
              onClick={() => handleCardClick(step.number)}
            >
              {/* Connecting line (desktop only) */}
              {step.number < displaySteps.length && (
                <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-cyan-400/50 to-transparent -z-10"
                  style={{ width: 'calc(100% - 3rem)', marginLeft: '1.5rem' }}
                ></div>
              )}

              <div className="text-left pt-5 pb-4 px-5 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-cyan-400/50 transition-all duration-300 h-full flex flex-col">
                {/* Header: Number + Title inline */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 bg-cyan-400/30 rounded-full blur-xl animate-pulse"></div>
                    <div className="relative bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full w-10 h-10 flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:scale-110 transition-transform duration-300">
                      {step.number}
                    </div>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white break-words">{step.title}</h3>
                </div>

                {/* Description */}
                <p className="text-gray-300 text-sm leading-relaxed mb-3 flex-1">{step.description}</p>

                {/* Stem Cell Animation - Card 1 always animates, Cards 2&3 static until clicked */}
                <div className="flex justify-center">
                  {isCard1 ? (
                    <StemCellAnimation size={70} speed={350} />
                  ) : (
                    <StemCellAnimation 
                      size={70} 
                      speed={143} 
                      static={!isCardAnimating}
                      animateOnce={isCardAnimating}
                      onAnimationComplete={isCardAnimating ? handleAnimationComplete : undefined}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal - EXACT replica of GetStartedBanner from bornfree-hub */}
      {showModal && (
        <div
          onClick={(e) => {
            // Close modal when clicking outside (on backdrop)
            if (e.target === e.currentTarget) {
              setShowModal(false);
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            backgroundColor: 'rgba(23, 27, 36, 0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            animation: 'slideDown 0.3s ease-out',
            overflowY: 'auto',
            paddingTop: '116px',
            paddingBottom: '24px'
          }}
        >
          <div
            className="kyc-modal-grid"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '1600px',
              margin: '0 auto',
              padding: '24px 16px 16px 16px',
              position: 'relative'
            }}
          >
            {/* Close Button - Positioned at top right of card 3 */}
            <button
              onClick={() => setShowModal(false)}
              className="modal-close-button"
              style={{
                position: 'absolute',
                top: '24px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                zIndex: 10001
              }}
              aria-label="Close modal"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ef4444';
                e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#9ca3af';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <XMarkIcon style={{ width: '24px', height: '24px' }} />
            </button>

            {/* Step 1: Join the Community - EXACT COPY */}
            <div
              className="modal-card"
              style={{
                padding: '24px',
                borderRadius: '16px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                position: 'relative',
                height: '100%'
              }}
            >
              {/* Header: Number + Title/Description */}
              <div className="modal-header-row">
                <div className="modal-number-circle">
                  <div className="modal-number-glow"></div>
                  <div className="modal-number">1</div>
                </div>
                <div className="modal-title-content">
                  <h3 
                    className="modal-step-title"
                    style={{
                      fontSize: titleFontSize, // JavaScript-controlled - inline styles have highest specificity
                      fontWeight: 'bold',
                      color: '#ffffff',
                      marginBottom: '8px',
                      lineHeight: '1.3',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                      width: '100%',
                      boxSizing: 'border-box'
                    } as React.CSSProperties}
                  >
                    Join the Community
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0, textAlign: 'left' }}>
                    Complete KYC for identity verification. Receive a Member ID then mint an Experience Key.
                  </p>
                </div>
              </div>

              {/* Visual: Decentralized ID - Teal Box Style */}
              <div style={{ padding: '20px', backgroundColor: 'rgba(34, 211, 238, 0.1)', borderRadius: '12px', border: '1px solid rgba(34, 211, 238, 0.2)', position: 'relative', overflow: 'hidden', minHeight: '200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', minHeight: '90px' }}>
                  <div 
                    style={{
                      width: '64px',
                      height: '64px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      flexShrink: 0,
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
                    }}
                  >
                    {pixelFaceDataUrl ? (
                      <img 
                        src={pixelFaceDataUrl}
                        alt="Decentralized ID"
                        style={{
                          width: '60px',
                          height: '60px',
                          imageRendering: 'pixelated',
                          borderRadius: '10px',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '60px',
                        height: '60px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '12px'
                      }}>
                        Loading...
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#ffffff', marginBottom: '6px', textAlign: 'left' }}>Your Decentralized ID</h4>
                    <p style={{ margin: 0, fontSize: '13px', color: '#d1d5db', lineHeight: '1.5', textAlign: 'left' }}>
                      A unique, generative ID that represents you in the BornFree ecosystem.{' '}
                      <span 
                        style={{ 
                          color: '#00b0db', 
                          cursor: 'pointer', 
                          textDecoration: 'underline',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}
                      >
                        How to get USDC?
                      </span>
                    </p>
                  </div>
                </div>

                {/* Get Member ID - Inside teal box */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid rgba(34, 211, 238, 0.2)' }}>
                  <div style={{ fontSize: '16px', color: '#ffffff', fontWeight: '600' }}>Get Member ID</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-8px', backgroundColor: 'rgba(34, 211, 238, 0.25)', borderRadius: '50%', filter: 'blur(10px)', animation: 'pulse 2s ease-in-out infinite' }}></div>
                      <User style={{ position: 'relative', width: '32px', height: '32px', color: '#22d3ee', strokeWidth: '2.5', filter: 'drop-shadow(0 0 10px rgba(34, 211, 238, 0.8))' }} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-8px', backgroundColor: 'rgba(34, 211, 238, 0.15)', borderRadius: '50%', filter: 'blur(6px)', animation: 'pulse 2.5s ease-in-out infinite' }}></div>
                      <User style={{ position: 'relative', width: '28px', height: '28px', color: '#22d3ee', strokeWidth: '2', opacity: 0.85, filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.7))' }} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-8px', backgroundColor: 'rgba(34, 211, 238, 0.1)', borderRadius: '50%', filter: 'blur(4px)', animation: 'pulse 3s ease-in-out infinite' }}></div>
                      <User style={{ position: 'relative', width: '24px', height: '24px', color: '#22d3ee', strokeWidth: '2', opacity: 0.7, filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.6))' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Explanatory Text */}
              <div style={{ flexShrink: 0, padding: '12px 16px', backgroundColor: 'rgba(34, 211, 238, 0.08)', borderRadius: '12px', border: '1px solid rgba(34, 211, 238, 0.15)', minHeight: '140px', display: 'flex', flexDirection: 'column', marginBottom: '0' }}>
                <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0, fontWeight: '400', marginBottom: '8px', flexShrink: 0, textAlign: 'left' }}>
                  Complete KYC verification to receive your unique Member ID. Mint your Experience Key to join the BornFree community and start earning credits.
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'disc', color: '#9ca3af', fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '1px', lineHeight: '1.3', flex: 1, textAlign: 'left' }}>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Private:</span> Your data, your control.</li>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Verifiable:</span> On-chain proof of identity.</li>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Customizable:</span> Upload your own artwork post-mint.</li>
                </ul>
              </div>

              {/* Get Member ID Button */}
              <button
                onClick={() => {
                  setShowModal(false);
                  // Navigate to app with KYC modal parameter - app.bornfree.io handles ?kyc=true to open KYC modal
                  window.open('https://app.bornfree.io/?kyc=true', '_blank');
                }}
                style={{
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '14px 24px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: '0 6px 20px rgba(37, 99, 235, 0.4)',
                  marginTop: 'auto'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1d4ed8';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(37, 99, 235, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                }}
              >
                Get a Member ID
                <ArrowRight style={{ width: '18px', height: '18px' }} />
              </button>
            </div>

            {/* Step 2: Get Your Credits - EXACT COPY */}
            <div
              className="modal-card"
              style={{
                padding: '24px',
                borderRadius: '16px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                position: 'relative',
                height: '100%'
              }}
            >
              {/* Header: Number + Title/Description */}
              <div className="modal-header-row">
                <div className="modal-number-circle">
                  <div className="modal-number-glow"></div>
                  <div className="modal-number">2</div>
                </div>
                <div className="modal-title-content">
                  <h3 
                    className="modal-step-title"
                    style={{
                      fontSize: titleFontSize, // JavaScript-controlled, bypasses CSS
                      fontWeight: 'bold',
                      color: '#ffffff',
                      marginBottom: '8px',
                      lineHeight: '1.3',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    Get Your Credits
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0, textAlign: 'left' }}>
                    Your Experience Key includes credits (200-20,000) that renew automatically each year.
                  </p>
                </div>
              </div>

              {/* Visual: Experience Key Asset with Credits - Teal Box */}
              <div style={{ padding: '20px', backgroundColor: 'rgba(34, 211, 238, 0.1)', borderRadius: '12px', border: '1px solid rgba(34, 211, 238, 0.2)', position: 'relative', overflow: 'hidden', minHeight: '200px' }}>
                {/* Experience Key Asset Display - Three images in horizontal layout */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ flex: 1, height: '90px', borderRadius: '8px', overflow: 'hidden', border: '2px solid rgba(34, 211, 238, 0.3)' }}>
                      <img 
                        src="https://bornfree.io/images/collections/genesis/preview1.webp"
                        alt="Genesis"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, height: '90px', borderRadius: '8px', overflow: 'hidden', border: '2px solid rgba(34, 211, 238, 0.3)' }}>
                      <img 
                        src="https://bornfree.io/images/collections/freedom/preview1.webp"
                        alt="Freedom"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, height: '90px', borderRadius: '8px', overflow: 'hidden', border: '2px solid rgba(34, 211, 238, 0.3)' }}>
                      <img 
                        src="https://bornfree.io/images/collections/keepitreal/preview1.webp"
                        alt="Keep It Real"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Credits flowing out */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid rgba(34, 211, 238, 0.2)' }}>
                  <div style={{ fontSize: '16px', color: '#ffffff', fontWeight: '600' }}>Credits</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-6px', backgroundColor: 'rgba(34, 211, 238, 0.2)', borderRadius: '50%', filter: 'blur(8px)', animation: 'pulse 2s ease-in-out infinite' }}></div>
                      <Coins style={{ position: 'relative', width: '32px', height: '32px', color: '#22d3ee', strokeWidth: '2.5', filter: 'drop-shadow(0 0 10px rgba(34, 211, 238, 0.8))' }} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-6px', backgroundColor: 'rgba(34, 211, 238, 0.15)', borderRadius: '50%', filter: 'blur(6px)', animation: 'pulse 2.5s ease-in-out infinite' }}></div>
                      <Coins style={{ position: 'relative', width: '28px', height: '28px', color: '#22d3ee', strokeWidth: '2', opacity: 0.85, filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.7))' }} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-6px', backgroundColor: 'rgba(34, 211, 238, 0.1)', borderRadius: '50%', filter: 'blur(4px)', animation: 'pulse 3s ease-in-out infinite' }}></div>
                      <Coins style={{ position: 'relative', width: '24px', height: '24px', color: '#22d3ee', strokeWidth: '2', opacity: 0.7, filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.6))' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Explanatory Text */}
              <div style={{ flexShrink: 0, padding: '12px 16px', backgroundColor: 'rgba(34, 211, 238, 0.08)', borderRadius: '12px', border: '1px solid rgba(34, 211, 238, 0.15)', minHeight: '140px', display: 'flex', flexDirection: 'column', marginBottom: '0' }}>
                <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0, fontWeight: '400', marginBottom: '8px', flexShrink: 0, textAlign: 'left' }}>
                  <span style={{ color: '#22d3ee', fontWeight: '600' }}>Choose</span> number of Nights/Credits to mint into your Experience Key. Purchase <span style={{ color: '#ffffff', fontWeight: '600' }}>7 nights at $500/night = $3,500</span>. Get <span style={{ color: '#22d3ee', fontWeight: '600' }}>3,500 credits</span> replenished each year.
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'disc', color: '#9ca3af', fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '1px', lineHeight: '1.3', flex: 1, textAlign: 'left' }}>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Renewable:</span> Credits automatically renew each year on January 8th.</li>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Transferable:</span> Trade credits with members on the marketplace.</li>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Universal:</span> Credits hold value across all experiences and locations.</li>
                </ul>
              </div>
            </div>

            {/* Step 3: Reserve & Connect - EXACT COPY */}
            <div
              className="modal-card"
              style={{
                padding: '24px',
                borderRadius: '16px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                position: 'relative',
                height: '100%'
              }}
            >
              {/* Header: Number + Title/Description */}
              <div className="modal-header-row">
                <div className="modal-number-circle">
                  <div className="modal-number-glow"></div>
                  <div className="modal-number">3</div>
                </div>
                <div className="modal-title-content">
                  <h3 
                    className="modal-step-title"
                    style={{
                      fontSize: titleFontSize, // JavaScript-controlled, bypasses CSS
                      fontWeight: 'bold',
                      color: '#ffffff',
                      marginBottom: '8px',
                      lineHeight: '1.3',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    Reserve & Connect
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0, textAlign: 'left' }}>
                    Use credits to reserve properties, trade with members, and vote on DAO direction and projects.
                  </p>
                </div>
              </div>

              {/* Visual: Property Images with Reservation Key - Teal Box */}
              <div style={{ padding: '20px', backgroundColor: 'rgba(34, 211, 238, 0.1)', borderRadius: '12px', border: '1px solid rgba(34, 211, 238, 0.2)', position: 'relative', overflow: 'hidden', minHeight: '200px' }}>
                {/* Property Images */}
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '90px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid rgba(34, 211, 238, 0.3)',
                    boxShadow: '0 4px 12px rgba(34, 211, 238, 0.2)',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                  }}>
                    <img 
                      src="https://bornfree.io/images/Properties/Hotel%20Posada%20Real.png"
                      alt="Hotel Posada Real"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '90px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid rgba(34, 211, 238, 0.3)',
                    boxShadow: '0 4px 12px rgba(34, 211, 238, 0.2)',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                  }}>
                    <img 
                      src="https://bornfree.io/images/9CAF1186-CC8D-4B0D-9DDF-7CB1A1EFEBEE.jpeg"
                      alt="BornFree Property"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
                
                {/* Reservation Key flowing out */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid rgba(34, 211, 238, 0.2)' }}>
                  <div style={{ fontSize: '16px', color: '#ffffff', fontWeight: '600' }}>Reservation Key</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-8px', backgroundColor: 'rgba(34, 211, 238, 0.25)', borderRadius: '50%', filter: 'blur(10px)', animation: 'pulse 2s ease-in-out infinite' }}></div>
                      <Key style={{ position: 'relative', width: '32px', height: '32px', color: '#22d3ee', strokeWidth: '2.5', filter: 'drop-shadow(0 0 10px rgba(34, 211, 238, 0.8))' }} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-8px', backgroundColor: 'rgba(34, 211, 238, 0.15)', borderRadius: '50%', filter: 'blur(6px)', animation: 'pulse 2.5s ease-in-out infinite' }}></div>
                      <Key style={{ position: 'relative', width: '28px', height: '28px', color: '#22d3ee', strokeWidth: '2', opacity: 0.85, filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.7))' }} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '-8px', backgroundColor: 'rgba(34, 211, 238, 0.1)', borderRadius: '50%', filter: 'blur(4px)', animation: 'pulse 3s ease-in-out infinite' }}></div>
                      <Key style={{ position: 'relative', width: '24px', height: '24px', color: '#22d3ee', strokeWidth: '2', opacity: 0.7, filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.6))' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Explanatory Text */}
              <div style={{ flexShrink: 0, padding: '12px 16px', backgroundColor: 'rgba(34, 211, 238, 0.08)', borderRadius: '12px', border: '1px solid rgba(34, 211, 238, 0.15)', minHeight: '140px', display: 'flex', flexDirection: 'column', marginBottom: '0' }}>
                <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0, fontWeight: '400', marginBottom: '8px', flexShrink: 0, textAlign: 'left' }}>
                  Use your credits to <span style={{ color: '#22d3ee', fontWeight: '600' }}>reserve a stay</span> at any property in our community network. <span style={{ color: '#ffffff', fontWeight: '600' }}>Connect and experience</span> the associated project and purpose events!
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'disc', color: '#9ca3af', fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '1px', lineHeight: '1.3', flex: 1, textAlign: 'left' }}>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Priority Access:</span> 200 days per year of reservation priority</li>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Network:</span> Access properties and experiences worldwide.</li>
                  <li style={{ textAlign: 'left' }}><span style={{ fontWeight: '600', color: '#9ca3af' }}>Community:</span> Vote on DAO direction and new ventures</li>
                </ul>
              </div>
            </div>
          </div>

          <style>{`
            .kyc-modal-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 20px;
              align-items: stretch;
            }
            
            /* Header row - horizontal by default */
            .modal-header-row {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              border-bottom: 1px solid rgba(255, 255, 255, 0.1);
              padding-bottom: 16px;
            }
            
            /* Number circle container */
            .modal-number-circle {
              position: relative;
              flex-shrink: 0;
            }
            
            .modal-number-glow {
              position: absolute;
              inset: 0;
              background-color: rgba(34, 211, 238, 0.3);
              border-radius: 50%;
              filter: blur(16px);
              animation: pulse 2s ease-in-out infinite;
            }
            
            .modal-number {
              position: relative;
              background: linear-gradient(to bottom right, #22d3ee, #2563eb);
              border-radius: 50%;
              width: 56px;
              height: 56px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #ffffff;
              font-weight: bold;
              font-size: 24px;
              box-shadow: 0 8px 24px rgba(34, 211, 238, 0.4);
            }
            
            /* Title content area - ensure it takes remaining space and wraps properly */
            .modal-title-content {
              flex: 1 1 0%;
              min-width: 0;
              max-width: 100%;
              overflow: hidden;
              box-sizing: border-box;
            }
            
            /* CRITICAL: Override Tailwind's @layer base h3 styles with maximum specificity */
            .modal-step-title.modal-step-title {
              all: unset !important;
              display: block !important;
              box-sizing: border-box !important;
              font-size: 16px !important; /* Base mobile size - override Tailwind */
              font-weight: bold !important;
              line-height: 1.3 !important;
            }
            
            /* Very small mobile - stack vertically, smaller number */
            @media (max-width: 400px) {
              .modal-header-row {
                flex-direction: column;
                align-items: center;
                text-align: center;
                gap: 12px;
              }
              .modal-number {
                width: 44px;
                height: 44px;
                font-size: 18px;
              }
              .modal-title-content {
                text-align: center;
                width: 100%;
              }
              .modal-step-title.modal-step-title {
                font-size: 14px !important;
              }
            }
            
            /* Small mobile - horizontal but smaller number */
            @media (min-width: 401px) and (max-width: 640px) {
              .modal-number {
                width: 44px;
                height: 44px;
                font-size: 18px;
              }
              .modal-header-row {
                gap: 10px;
              }
              .modal-step-title.modal-step-title {
                font-size: 15px !important;
              }
            }
            
            /* Large mobile - slightly larger number */
            @media (min-width: 641px) and (max-width: 767px) {
              .modal-number {
                width: 48px;
                height: 48px;
                font-size: 20px;
              }
              .modal-header-row {
                gap: 12px;
              }
              .modal-step-title.modal-step-title {
                font-size: 16px !important;
              }
            }
            
            /* Tablet - 3 columns with smaller number and reduced padding */
            @media (min-width: 768px) and (max-width: 1023px) {
              .kyc-modal-grid {
                grid-template-columns: 1fr 1fr 1fr;
                gap: 8px;
                padding: 12px 8px;
              }
              .modal-card {
                padding: 12px !important;
              }
              .modal-number {
                width: 36px;
                height: 36px;
                font-size: 16px;
              }
              .modal-header-row {
                gap: 6px;
                padding-bottom: 12px;
              }
              .modal-title-content {
                flex: 1 1 auto;
                min-width: 0;
              }
              /* CRITICAL: Override Tailwind's md:text-3xl (30px) - force smaller font for narrow cards */
              .modal-step-title.modal-step-title {
                font-size: 15px !important;
                max-width: 100% !important;
              }
            }
            
            /* Large tablets - 3 columns with medium number */
            @media (min-width: 1024px) {
              .kyc-modal-grid {
                grid-template-columns: 1fr 1fr 1fr;
                gap: 20px;
                position: relative;
              }
              .modal-header-row {
                gap: 16px;
              }
              /* Position close button at top right of card 3 - align with card 3's right edge */
              .modal-close-button {
                top: 24px !important;
                right: 16px !important;
              }
            }
            
            @keyframes slideDown {
              from {
                transform: translateY(-100%);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            @keyframes pulse {
              0%, 100% {
                opacity: 0.3;
              }
              50% {
                opacity: 0.6;
              }
            }
          `}</style>
        </div>
      )}
    </>
  );
};

export default ThreeStepGuide;
